import React, { useState, useCallback } from 'react';
import he from 'he';
import { array, arrayOf, bool, func, shape, string, oneOf, object } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { requestUpdateStateCityForListing } from '../EditListingPage/EditListingPage.duck';

// Contexts
import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
// Utils
import { FormattedMessage, intlShape, useIntl } from '../../util/reactIntl';
import { LISTING_STATE_PENDING_APPROVAL, LISTING_STATE_CLOSED, propTypes } from '../../util/types';
import { types as sdkTypes } from '../../util/sdkLoader';
import {
  LISTING_PAGE_DRAFT_VARIANT,
  LISTING_PAGE_PENDING_APPROVAL_VARIANT,
  LISTING_PAGE_PARAM_TYPE_DRAFT,
  LISTING_PAGE_PARAM_TYPE_EDIT,
  createSlug,
  NO_ACCESS_PAGE_USER_PENDING_APPROVAL,
  NO_ACCESS_PAGE_VIEW_LISTINGS,
} from '../../util/urlHelpers';
import {
  isErrorNoViewingPermission,
  isErrorUserPendingApproval,
  isForbiddenError,
} from '../../util/errors.js';
import { hasPermissionToViewData, isUserAuthorized } from '../../util/userHelpers.js';
import {
  ensureListing,
  ensureOwnListing,
  ensureUser,
  userDisplayNameAsString,
} from '../../util/data';
import { richText } from '../../util/richText';
import {
  isBookingProcess,
  isPurchaseProcess,
  resolveLatestProcessName,
} from '../../transactions/transaction';

// Global ducks (for Redux actions and thunks)
import { getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { manageDisableScrolling, isScrollingDisabled } from '../../ducks/ui.duck';
import { initializeCardPaymentData } from '../../ducks/stripe.duck.js';

// Shared components
import {
  H4,
  Page,
  NamedLink,
  NamedRedirect,
  OrderPanel,
  LayoutSingleColumn,
  ListingClaimModal,
  Button,
  Avatar,
  PrimaryButton,
  ListingCard,
  ReviewRating,
  ExternalLink,
} from '../../components';

// Related components and modules
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import NotFoundPage from '../NotFoundPage/NotFoundPage';

import {
  sendInquiry,
  setInitialValues,
  fetchTimeSlots,
  fetchTransactionLineItems,
  trackListingAnalyticsEvent, // ✅ NEW
} from './ListingPage.duck';

import {
  LoadingPage,
  ErrorPage,
  priceData,
  listingImages,
  handleContactUser,
  handleSubmitInquiry,
  handleSubmit,
  priceForSchemaMaybe,
} from './ListingPage.shared';
import ActionBarMaybe from './ActionBarMaybe';
import SectionTextMaybe from './SectionTextMaybe';
import SectionOptionalExtras from './SectionOptionalExtras';
import SectionReviews from './SectionReviews';
import SectionAuthorMaybe from './SectionAuthorMaybe';
import SectionMapMaybe from './SectionMapMaybe';
import SectionGallery from './SectionGallery';
import CustomListingFields from './CustomListingFields';
import SectionCancellation from './SectionCancellation.js';
import { buildSpecItems, buildRundownText } from '../../util/listingSpecs';

import { pickCategoryFields, isFieldForCategory } from '../../util/fieldHelpers';
import { withViewport } from '../../util/uiHelpers.js';
import SectionBreadcrumbs from './SectionBreadcrumbs.js';
// import SectionSideNavigation from './SectionSideNavigation.js';

// Haul Star conversion-redesign sections (2026-09-12).
import SectionSpecialMaybe from './SectionSpecialMaybe';
import SectionReadyToRent from './SectionReadyToRent';
import SectionStickyContactBar from './SectionStickyContactBar';

import css from './ListingPage.module.css';
import rd from './ListingRedesign.module.css';
import { trackGA4Event, GA4_EVENTS } from '../../analytics/ga4';
import { trackFacebookEvent, getFbCookies } from '../../analytics/facebookPixel';
import Icons from '../../components/Icons/Icons.js';
import {
  getAuthorSubscriptionData,
  getSubscription,
  getSubscriptionProductData,
} from '../../util/subscriptionHelpers.js';
import { SUBSCRIPTION_PLAN_NAMES } from '../../util/enums.js';
import {
  extractListingAuthorInfo,
  formatTelLink,
  formatSmsLink,
  getSubscriptionIcon,
  getSubscriptionLabel,
  getUserGoogleReviews,
  isArrayLength,
  isHaulStarPlan,
  normalizeUrl,
} from '../../util/genericHelpers.js';
import { cancelSubscription } from '../SubscriptionPage/SubscriptionPage.duck.js';
import { fetchCurrentUser } from '../../ducks/user.duck.js';
import SectionSubscriptionPlans from './SectionSubscription/Sections/SectionSubscriptionPlans.js';

// React Icons for social media
import {
  FaInstagram,
  FaFacebookF,
  FaTiktok,
  FaYoutube,
  FaLinkedinIn,
  FaTwitter,
  FaPinterestP,
  FaSnapchatGhost,
  FaGlobe,
} from 'react-icons/fa';
import { cardRenderSizes } from '../SearchPage/SearchResultsPanel/SearchResultsPanel.js';

import { formatMoney } from '../../util/currency.js';

const MIN_LENGTH_FOR_LONG_WORDS_IN_TITLE = 16;

// Decode HTML entities (e.g. &amp;, &#x27;) for use in <title>/<h1>/og:title/meta.
// React still escapes the decoded output on render, so this stays XSS-safe.
const decodeEntities = str => {
  if (!str || typeof str !== 'string') return str || '';
  try {
    return he.decode(str);
  } catch (e) {
    return str;
  }
};

// Strip HTML tags + collapse whitespace + decode entities.
const stripHtml = str =>
  decodeEntities(String(str || '').replace(/<[^>]*>/g, ' '))
    .replace(/\s+/g, ' ')
    .trim();

// Word-safe truncate to maxLen chars (no mid-word cut, adds ellipsis).
const truncateWordSafe = (str, maxLen = 155) => {
  const s = String(str || '').trim();
  if (s.length <= maxLen) return s;
  const cut = s.slice(0, maxLen);
  const lastSpace = cut.lastIndexOf(' ');
  const base = lastSpace > 40 ? cut.slice(0, lastSpace) : cut;
  return base.replace(/[\s.,;:!-]+$/, '') + '…';
};

const { UUID, Money } = sdkTypes;

const toMoney = (subunits, config) => new Money(Number(subunits) || 0, config.currency);

// ✅ Analytics event constants
const LISTING_ANALYTICS_EVENTS = {
  VIEW: 'view',
  CLICK_PHONE: 'click_phone',
  CLICK_EMAIL: 'click_email',
  CLICK_WEBSITE: 'click_website',
  CLICK_SOCIAL: 'click_social',
};

// ✅ Referrer domain helper
const getReferrerDomain = () => {
  try {
    if (typeof document === 'undefined') return null;
    const ref = document.referrer || '';
    if (!ref) return null;
    return new URL(ref).hostname || null;
  } catch (e) {
    return null;
  }
};

// Prefer per-day if present, otherwise use lowest positive price as starting.
const getStartingPrice = pricingArr => {
  if (!Array.isArray(pricingArr) || pricingArr.length === 0) return null;

  const perDay = pricingArr.find(p => p?.key === 'perDayPrice' && Number(p?.price) > 0);
  if (perDay) return { ...perDay, label: perDay?.name || 'Per day' };

  const valid = pricingArr
    .filter(p => Number(p?.price) > 0)
    .sort((a, b) => Number(a?.price) - Number(b?.price));

  if (!valid.length) return null;

  return { ...valid[0], label: valid[0]?.name || '' };
};

// Sum a listing's stored per-day view counts over the trailing N days. Data
// already lives in listing.attributes.metadata.analytics.views.byDay (written
// by the existing analytics tracker) so this is a free social-proof number, no
// new tracking/endpoint. Returns 0 when unavailable.
const getViewsInLastDays = (currentListing, days = 7) => {
  try {
    const byDay = currentListing?.attributes?.metadata?.analytics?.views?.byDay;
    if (!byDay || typeof byDay !== 'object') return 0;
    // The server writes byDay keys as UTC date strings (getDayKeyUTC), so the
    // cutoff MUST be computed in UTC too, otherwise a browser far from UTC can
    // include/drop a boundary day. Compare on UTC midnight.
    const now = new Date();
    const cutoff = Date.UTC(
      now.getUTCFullYear(),
      now.getUTCMonth(),
      now.getUTCDate() - (days - 1)
    );
    let sum = 0;
    Object.keys(byDay).forEach(k => {
      const d = new Date(`${k}T00:00:00Z`);
      if (!Number.isNaN(d.getTime()) && d.getTime() >= cutoff) {
        const n = Number(byDay[k]);
        if (Number.isFinite(n) && n > 0) sum += n;
      }
    });
    return sum;
  } catch (e) {
    return 0;
  }
};

// Only surface the viewed-count as social proof once it reads as popular; below
// this it would look like "nobody wants this", so we hide it entirely.
const VIEWS_DISPLAY_THRESHOLD = 8;

// Format an owner-supplied insurance-policy expiry ("YYYY-MM-DD" or any
// Date-parseable string) into a friendly "Mon YYYY" for the Insured Business
// pill. Returns '' on anything unparseable so we never render a broken date.
const formatInsuranceExpiry = value => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  try {
    return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  } catch (e) {
    return '';
  }
};

export const getSocialIconComponent = platform => {
  const p = (platform || '').toLowerCase();

  switch (p) {
    case 'instagram':
      return FaInstagram;
    case 'facebook':
      return FaFacebookF;
    case 'tiktok':
      return FaTiktok;
    case 'youtube':
      return FaYoutube;
    case 'linkedin':
      return FaLinkedinIn;
    case 'twitter':
      return FaTwitter;
    case 'pinterest':
      return FaPinterestP;
    case 'snapchat':
      return FaSnapchatGhost;
    default:
      return FaGlobe;
  }
};

export const ListingPageComponent = props => {
  const [inquiryModalOpen, setInquiryModalOpen] = useState(
    props.inquiryModalOpenForListingId === props.params.id
  );
  const [isClaimListingModalOpen, setIsClaimListingModalOpen] = useState(false);

  const {
    isAuthenticated,
    currentUser,
    getListing,
    getOwnListing,
    intl,
    onManageDisableScrolling,
    params: rawParams,
    location,
    scrollingDisabled,
    showListingError,
    reviews,
    fetchReviewsError,
    sendInquiryInProgress,
    sendInquiryError,
    monthlyTimeSlots,
    onFetchTimeSlots,
    onFetchTransactionLineItems,
    lineItems,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    viewport,
    history,
    callSetInitialValues,
    onSendInquiry,
    onInitializeCardPaymentData,
    config,
    routeConfiguration,
    showOwnListingsOnly,
    userDetails,
    productsLoading,
    products,
    productsError,
    subscriptionCancelInProgress,
    onCancelSubscription,
    onFetchCurrentUser,
    similarListings, // <- NEW
  } = props;

  const isTabLayout = viewport.width > 0 && viewport.width < 1024;
  const isMobileLayout = viewport.width > 0 && viewport.width < 768; // mobile breakpoint

  const listingConfig = config.listing;
  const listingId = new UUID(rawParams.id);
  const isValidClaimPage = location?.pathname?.includes('/claim/');

  const isPendingApprovalVariant = rawParams.variant === LISTING_PAGE_PENDING_APPROVAL_VARIANT;
  const isDraftVariant = rawParams.variant === LISTING_PAGE_DRAFT_VARIANT;

  const currentListing =
    isPendingApprovalVariant || isDraftVariant || showOwnListingsOnly
      ? ensureOwnListing(getOwnListing(listingId))
      : ensureListing(getListing(listingId));

  // === AUTO-FIX: Patch missing city/state at view-time ===
  const dispatch = useDispatch();

  React.useEffect(() => {
    if (!currentListing || !currentListing.attributes) return;

    const pub = currentListing.attributes.publicData || {};
    const address = pub?.location?.address;
    const hasCity = !!pub.city;
    const hasState = !!pub.state;

    // Only fix if both are missing
    if (!address || hasCity || hasState) return;

    const parts = address.split(',');
    if (parts.length < 2) return;

    const parsedCity = parts[0].trim();
    const parsedState = parts[1].trim().split(' ')[0];

    dispatch(
      requestUpdateStateCityForListing({
        id: currentListing.id.uuid,
        city: parsedCity.toLowerCase(),
        state: parsedState.toLowerCase(),
      })
    );
  }, [currentListing, dispatch]);
  // === END AUTO-FIX ===

  // Cheap bot/crawler filter so Googlebot / Meta link-preview crawlers don't
  // inflate the Haul Star proof stats or fire fake CAPI conversions.
  const isLikelyBot = () => {
    if (typeof navigator === 'undefined') return true; // no browser = don't track
    const ua = (navigator.userAgent || '').toLowerCase();
    if (!ua) return true;
    return /bot|crawl|spider|slurp|bingpreview|facebookexternalhit|facebot|embedly|quora link preview|pinterest|whatsapp|telegrambot|headless|lighthouse|gtmetrix|pingdom|monitor|preview/.test(
      ua
    );
  };

  // Is the viewer the owner of this listing? Computed inline because the
  // page-level `isOwnListing` is defined later in the component. Owner
  // self-views/clicks must NOT count as conversions or burn SDK writes.
  const viewerIsOwner = () => {
    try {
      const authorUuid = currentListing?.author?.id?.uuid;
      const meUuid = currentUser?.id?.uuid;
      return !!(authorUuid && meUuid && authorUuid === meUuid);
    } catch (e) {
      return false;
    }
  };

  // ✅ Analytics: one function to track all events
  const trackEvent = (eventType, extra = {}) => {
    const listingUuid = currentListing?.id?.uuid;
    if (!listingUuid) return;

    // Guard: skip owner self-traffic + bots/crawlers entirely (no counter,
    // no GA4, no pixel, no CAPI). Keeps the proof stats + Meta signal clean.
    if (viewerIsOwner() || isLikelyBot()) return;

    const pd = currentListing?.attributes?.publicData || {};
    // Haul Star / Big Rig = paid tiers. This flag drives the Meta custom
    // conversions + the cherry-pickable per-member proof stats.
    const subName = (pd.subscriptionProductName || pd.subscriptionName || '').toLowerCase();
    const isHaulStar = subName.includes('haul star') || subName.includes('big rig');

    // VIEW de-dup: collapse repeat views of the same listing within a session
    // (remounts / client-side nav back) to ONE conversion. Clicks always fire.
    if (eventType === LISTING_ANALYTICS_EVENTS.VIEW && typeof window !== 'undefined') {
      try {
        const key = `bl_view_${listingUuid}`;
        const seen = window.sessionStorage.getItem(key);
        const now = Date.now();
        // 30-min TTL so a genuinely new session/visit still counts.
        if (seen && now - Number(seen) < 30 * 60 * 1000) return;
        window.sessionStorage.setItem(key, String(now));
      } catch (e) {
        /* sessionStorage unavailable (private mode) -> just proceed */
      }
    }

    // Shared event_id so the browser pixel event + the server-side CAPI event
    // dedup to ONE conversion in Meta.
    const eventId =
      typeof window !== 'undefined' && window.crypto && window.crypto.randomUUID
        ? window.crypto.randomUUID()
        : `bl-${listingUuid}-${eventType}-${Date.now()}-${Math.random().toString(36).slice(2)}`;

    // Existing custom backend counter (powers the owner dashboard stats) +
    // now also carries the CAPI context (eventId, isHaulStar, fb cookies) so the
    // server can fire the matching Conversions API event authoritatively.
    const fbCookies = getFbCookies();
    dispatch(
      trackListingAnalyticsEvent({
        listingId: listingUuid,
        eventType,
        referrerDomain: getReferrerDomain(),
        // --- Meta CAPI passthrough ---
        eventId,
        isHaulStar,
        listingCity: pd.city || null,
        listingState: pd.state || null,
        fbp: fbCookies.fbp,
        fbc: fbCookies.fbc,
        pageUrl: typeof window !== 'undefined' ? window.location.href : null,
        ...extra,
      })
    );

    // Mirror to GA4 (separate system; owner dashboard is unaffected).
    // VIEW -> view_listing (engagement); phone/email/website clicks are
    // Haul Star direct-contact intent -> contact_owner (a lead signal).
    const ga4Params = {
      listing_id: listingUuid,
      trailer_type: Array.isArray(pd.trailerType) ? pd.trailerType[0] : pd.trailerType,
      listing_state: pd.state,
      listing_city: pd.city,
      // Tier flag so GA4 can split views + contacts by paid vs free with zero
      // joins. String because GA4 custom dimensions are strings. Mirrors the
      // is_haul_star already sent to Meta above.
      is_haul_star: isHaulStar ? 'true' : 'false',
    };

    // Mirror to the Meta pixel (browser). Standard events (ViewContent/Contact)
    // with is_haul_star + contact_method params so custom conversions can slice
    // by paid-tier + action. Same eventId as the CAPI event above = dedup.
    const metaCustomData = {
      content_type: 'listing',
      content_ids: [listingUuid],
      is_haul_star: isHaulStar,
      listing_city: pd.city || undefined,
      listing_state: pd.state || undefined,
      trailer_type: Array.isArray(pd.trailerType) ? pd.trailerType[0] : pd.trailerType,
    };

    if (eventType === LISTING_ANALYTICS_EVENTS.VIEW) {
      trackGA4Event(GA4_EVENTS.VIEW_LISTING, ga4Params);
      trackFacebookEvent('ViewContent', metaCustomData, eventId);
    } else {
      // click_phone / click_email / click_website / click_social
      // eventType is like 'click_phone' -> contact_method 'phone'. A caller can
      // pass extra.method (e.g. 'text' for the SMS deep-link) to distinguish it
      // from a phone call while reusing the same CLICK_PHONE event.
      const baseMethod = eventType.replace(/^click_/, '');
      const method = extra && extra.method === 'text' ? 'sms' : baseMethod;
      const platform = extra && extra.platform ? `_${extra.platform}` : '';
      const contactMethod = `${method}${platform}`;
      trackGA4Event(GA4_EVENTS.CONTACT_OWNER, { ...ga4Params, contact_method: contactMethod });
      trackFacebookEvent(
        'Contact',
        { ...metaCustomData, contact_method: contactMethod },
        eventId
      );
    }
  };

  React.useEffect(() => {
    if (!currentListing?.id?.uuid) return;
    trackEvent(LISTING_ANALYTICS_EVENTS.VIEW);
  }, [currentListing?.id?.uuid]);

  const listingSlug = rawParams.slug || createSlug(currentListing.attributes.title || '');
  const params = { slug: listingSlug, ...rawParams };

  const listingPathParamType = isDraftVariant
    ? LISTING_PAGE_PARAM_TYPE_DRAFT
    : LISTING_PAGE_PARAM_TYPE_EDIT;
  const listingTab = isDraftVariant ? 'photos' : 'details';

  const isApproved =
    currentListing.id && currentListing.attributes.state !== LISTING_STATE_PENDING_APPROVAL;

  const pendingIsApproved = isPendingApprovalVariant && isApproved;

  const pendingOtherUsersListing =
    (isPendingApprovalVariant || isDraftVariant) &&
    showListingError &&
    showListingError.status === 403;

  const shouldShowPublicListingPage = pendingIsApproved || pendingOtherUsersListing;

  if (shouldShowPublicListingPage) {
    return <NamedRedirect name="ListingPage" params={params} search={location.search} />;
  }

  const topbar = <TopbarContainer />;

  if (showListingError && showListingError.status === 404) {
    return <NotFoundPage staticContext={props.staticContext} />;
  } else if (showListingError) {
    return <ErrorPage topbar={topbar} scrollingDisabled={scrollingDisabled} intl={intl} />;
  } else if (!currentListing.id) {
    return <LoadingPage topbar={topbar} scrollingDisabled={scrollingDisabled} intl={intl} />;
  }

  const {
    description = '',
    geolocation = null,
    price = null,
    title = '',
    publicData = {},
    metadata = {},
  } = currentListing.attributes;

  const richTitle = (
    <span>
      {richText(title, {
        longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS_IN_TITLE,
        longWordClass: css.longWord,
      })}
    </span>
  );

  const authorAvailable = currentListing && currentListing.author;
  const userAndListingAuthorAvailable = !!(currentUser && authorAvailable);

  const isOwnListing =
    userAndListingAuthorAvailable && currentListing.author.id.uuid === currentUser.id.uuid;

  const { listingType, transactionProcessAlias, unitType } = publicData;

  if (!(listingType && transactionProcessAlias && unitType)) {
    return (
      <ErrorPage topbar={topbar} scrollingDisabled={scrollingDisabled} intl={intl} invalidListing />
    );
  }

  const processName = resolveLatestProcessName(transactionProcessAlias.split('/')[0]);
  const isBooking = isBookingProcess(processName);
  const isPurchase = isPurchaseProcess(processName);
  const processType = isBooking ? 'booking' : isPurchase ? 'purchase' : 'inquiry';

  const currentAuthor = authorAvailable ? currentListing.author : null;
  const ensuredAuthor = ensureUser(currentAuthor);

  const savedGoogleReviews = getUserGoogleReviews(ensuredAuthor);
  const reviewCount = savedGoogleReviews?.total;
  // Prefer the stored business average (Outscraper's true rating). We only
  // persist the newest N review objects, so computing (sum of stored ratings /
  // total) would UNDERSTATE the average whenever total > stored count. Fall back
  // to the legacy computed average only for old records without averageRating,
  // and in that case divide by the number of stored reviews (not the true total).
  const storedReviews = savedGoogleReviews?.reviews || [];
  const storedAverage =
    typeof savedGoogleReviews?.averageRating === 'number' && savedGoogleReviews.averageRating > 0
      ? savedGoogleReviews.averageRating
      : null;
  const legacyAverage =
    storedReviews.length > 0
      ? storedReviews.reduce((sum, review) => sum + (review.rating || 0), 0) / storedReviews.length
      : null;
  const avgRatingValue = storedAverage != null ? storedAverage : legacyAverage;
  const avgRating =
    reviewCount > 0 && avgRatingValue != null ? Number(avgRatingValue).toFixed(1) : null;
  // Business name + Google source URL so viewers can VERIFY the rating belongs
  // to this owner's actual business (guards against pasting an unrelated URL).
  const googleBusinessName = savedGoogleReviews?.businessName || null;
  const googleSourceUrl =
    typeof savedGoogleReviews?.sourceUrl === 'string' &&
    /^https:\/\//i.test(savedGoogleReviews.sourceUrl)
      ? savedGoogleReviews.sourceUrl
      : null;

  const noPayoutDetailsSetWithOwnListing =
    isOwnListing && processType !== 'inquiry' && !currentUser?.attributes?.stripeConnected;

  const payoutDetailsWarning = noPayoutDetailsSetWithOwnListing ? (
    <span className={css.payoutDetailsWarning}>
      <FormattedMessage id="ListingPage.payoutDetailsWarning" values={{ processType }} />
      <NamedLink name="StripePayoutPage">
        <FormattedMessage id="ListingPage.payoutDetailsWarningLink" />
      </NamedLink>
    </span>
  ) : null;

  const authorDisplayName = userDisplayNameAsString(ensuredAuthor, '');

  // Keep this for schemaTitle / SEO. (Does not affect the visible "Starting at" text.)
  const { formattedPrice } = priceData(price, config.currency, intl);

  // --- NEW: Starting at pricing text (single-line) ---
  const pricing = Array.isArray(publicData?.pricing) ? publicData.pricing : [];
  const starting = getStartingPrice(pricing);

  // Client requested: “Starting at $XXX Per Day” (single line)
  const startingPriceText = starting
    ? `Starting at ${formatMoney(intl, toMoney(starting.price, config))} Per Day`
    : null;

  const commonParams = { params, history, routes: routeConfiguration };

  const onContactUser = handleContactUser({
    ...commonParams,
    currentUser,
    callSetInitialValues,
    location,
    setInitialValues: props.callSetInitialValues ? setInitialValues : setInitialValues,
    setInquiryModalOpen,
    listingPublicData: currentListing?.attributes?.publicData || {},
  });

  const onSubmitInquiry = handleSubmitInquiry({
    ...commonParams,
    getListing,
    onSendInquiry,
    setInquiryModalOpen,
  });

  const onSubmit = handleSubmit({
    ...commonParams,
    currentUser,
    callSetInitialValues,
    getListing,
    onInitializeCardPaymentData,
  });

  const handleOrderSubmit = values => {
    const isCurrentlyClosed = currentListing.attributes.state === LISTING_STATE_CLOSED;
    if (isOwnListing || isCurrentlyClosed) {
      window.scrollTo(0, 0);
    } else {
      onSubmit(values);
    }
  };

  // === CANONICAL URL (SEO foundation, 2026-07-26) ===
  // Canonical form = BARE `/l/:slug/:id`. Decided by the All-Star team for DATA
  // INTEGRITY: city/state are null/dirty on some listings, so geo-in-path could
  // emit `/l/null/null/...` or flip when a lister edits their city = rolling
  // duplicate-content churn. slug+uuid is always constructible and the uuid
  // resolves routing regardless of slug edits. Real local signal comes from the
  // city/state hub internal links + on-page title/H1/location + geo schema.
  //
  // Canonical slug is ALWAYS derived from the current title (createSlug is
  // idempotent), never from the incoming URL. This gives slug-mismatch
  // correction for free: `/l/old-slug/:id` 301s to `/l/current-slug/:id`, while
  // `/l/current-slug/:id` equals canonicalPathname and does NOT re-redirect.
  const canonicalSlug = createSlug(currentListing.attributes.title || '') || listingSlug;
  const canonicalPathname = `/l/${canonicalSlug}/${currentListing.id.uuid}`;
  const canonicalURL = `${config.marketplaceRootURL}${canonicalPathname}`;

  // === 301 CANONICAL REDIRECT (SEO foundation, 2026-07-26) ===
  // Every non-canonical PUBLIC listing-view URL (geo variants, bare-id, and
  // slug-mismatch) 301s to the bare canonical `/l/:slug/:id`. This ends the
  // duplicate-content problem (same listing 200ing at 5+ URL shapes).
  //
  // SAFETY: this MUST NOT fire on any functional listing route that happens to
  // share the `/l/` prefix. Most (CheckoutPage, EditListingPage) render OTHER
  // components and never reach this code. The ones that DO use this component
  // (claim, draft/pending-approval variants) are excluded explicitly via the
  // param/flag checks below. Owner views are intentionally NOT special-cased:
  // canonicalizing a public listing URL is viewer-independent and correct.
  const currentPathname = (() => {
    try {
      return decodeURIComponent(location?.pathname || '');
    } catch (e) {
      return location?.pathname || '';
    }
  })().replace(/\/+$/, '');

  // Segment-anchored (NOT substring) so a slug like "credit-trailer",
  // "new-holland", or "draft-horse" can never falsely match "edit"/"new"/"draft"
  // and suppress a legitimate SEO 301. Only matches the real functional
  // sub-paths that hang off `/l/:slug/:id/...` (checkout, edit tabs).
  const isCheckoutOrEditPath = /^\/l\/[^/]+\/[^/]+\/(?:order\/checkout|edit)(?:\/|$)/.test(
    currentPathname
  );

  const isFunctionalListingRoute =
    isValidClaimPage ||
    isDraftVariant ||
    isPendingApprovalVariant ||
    !!rawParams.variant ||
    isCheckoutOrEditPath ||
    // any path with more segments than the canonical view forms (e.g. edit tabs)
    /^\/l\/[^/]+\/[^/]+\/[^/]+\/[^/]+\/(?!view$)/.test(currentPathname);

  const isCanonicalAlready = currentPathname === canonicalPathname;

  const shouldCanonicalRedirect =
    !isFunctionalListingRoute && !isCanonicalAlready && !!canonicalSlug && !!currentListing.id?.uuid;

  if (shouldCanonicalRedirect) {
    // Signal a 301 (not the default 302) to the SSR server. `staticContext` is
    // only defined during server render; on the client this is a harmless no-op
    // and React Router performs a normal client-side replace. Target uses
    // canonicalSlug so we land EXACTLY on canonicalPathname (single hop).
    if (props.staticContext) {
      props.staticContext.status = 301;
    }
    return (
      <NamedRedirect
        name="ListingPage"
        params={{ slug: canonicalSlug, id: currentListing.id.uuid }}
      />
    );
  }

  const facebookImages = listingImages(currentListing, 'facebook');
  const twitterImages = listingImages(currentListing, 'twitter');

  const schemaImages = listingImages(
    currentListing,
    `${config.layout.listingImage.variantPrefix}-2x`
  ).map(img => img.url);

  const marketplaceName = config.marketplaceName;

  // Raw (un-slugified) city/state for human-facing meta/title/schema.
  // Null-safe: never emit "null, null" or "null - null".
  const rawCity = publicData?.city ? String(publicData.city).trim() : '';
  const rawState = publicData?.state ? String(publicData.state).trim() : '';
  const locationLabel = [rawCity, rawState].filter(Boolean).join(', ');

  const decodedTitle = decodeEntities(title);
  const titleWithLocation = locationLabel
    ? `${decodedTitle} in ${locationLabel}`
    : decodedTitle;

  const schemaTitle = intl.formatMessage(
    { id: 'ListingPage.schemaTitle' },
    { title: titleWithLocation, price: formattedPrice, marketplaceName }
  );

  // === META DESCRIPTION (SEO, ~155 chars, templated + word-safe) ===
  // Prefer a compact structured summary; fall back to the (stripped) description.
  const specYear = publicData?.year || publicData?.Year || '';
  const specMake = publicData?.make || publicData?.Make || '';
  const specModel = publicData?.model || publicData?.Model || '';
  const specLength = publicData?.length || publicData?.Length || '';
  const specWeight = publicData?.weight || publicData?.Weight || '';
  const specHitch = publicData?.hitch || publicData?.hitchType || publicData?.Hitch || '';

  const priceForDesc = formattedPrice ? `${formattedPrice}/day` : '';
  const summaryParts = [specYear, specMake, specModel].filter(Boolean).join(' ').trim();
  const templatedDesc = [
    summaryParts ? `${summaryParts} trailer for rent` : 'Trailer for rent',
    locationLabel ? `in ${locationLabel}` : '',
    priceForDesc ? `from ${priceForDesc}.` : '.',
    'Book on BigLoads.',
  ]
    .filter(Boolean)
    .join(' ')
    .replace(/\s+\./g, '.')
    .trim();

  const metaDescription = truncateWordSafe(
    templatedDesc && summaryParts ? templatedDesc : stripHtml(description) || templatedDesc,
    155
  );

  const productURL = canonicalURL;

  const currentStock = currentListing.currentStock?.attributes?.quantity || 0;

  const schemaAvailability = !currentListing.currentStock
    ? null
    : currentStock > 0
    ? 'https://schema.org/InStock'
    : 'https://schema.org/OutOfStock';

  const availabilityMaybe = schemaAvailability ? { availability: schemaAvailability } : {};

  // === ENRICHED JSON-LD Product (SEO) ===
  // Built from the SAME shared util that renders the visible Trailer Details
  // cards (SectionDetailsMaybe), so structured data == on-page content 1:1.
  const specCategoriesObj = pickCategoryFields(
    publicData,
    config.categoryConfiguration?.key,
    1,
    config.categoryConfiguration?.categories
  );
  const specCategories = Object.values(specCategoriesObj);
  const isFieldForSpecCategory = fieldConfig => isFieldForCategory(specCategories, fieldConfig);
  const specItemsForSchema = buildSpecItems({
    publicData,
    metadata,
    listingFieldConfigs: listingConfig?.listingFields,
    isFieldForCategory: isFieldForSpecCategory,
    intl,
  });
  const additionalProperties = specItemsForSchema.map(s => ({
    '@type': 'PropertyValue',
    name: s.label,
    value: String(s.value),
  }));

  // "The Rundown" plain text (same content the visible <p> renders) reused as
  // the richer JSON-LD Product.description when available — keeps structured
  // data in sync with on-page content and gives Google unique per-listing prose
  // instead of the terse templated meta description.
  const rundownForSchema = buildRundownText({
    publicData,
    title,
    priceText: formattedPrice,
    priceUnit: 'day',
    listingId: listingId?.uuid,
  });
  const schemaDescription =
    (rundownForSchema && rundownForSchema.plainText) || metaDescription;

  const priceAmount =
    price && price.amount != null ? (Number(price.amount) / 100).toFixed(2) : null;
  const priceCurrency = price && price.currency ? price.currency : config.currency;

  const offerSchema = {
    '@type': 'Offer',
    url: productURL,
    ...(priceAmount
      ? {
          price: priceAmount,
          priceCurrency,
          priceSpecification: {
            '@type': 'UnitPriceSpecification',
            price: priceAmount,
            priceCurrency,
            unitCode: 'DAY',
          },
        }
      : priceForSchemaMaybe(price, intl)),
    ...availabilityMaybe,
    ...(authorDisplayName
      ? { seller: { '@type': 'Organization', name: authorDisplayName } }
      : {}),
  };

  const productSchema = {
    '@context': 'http://schema.org',
    '@type': 'Product',
    name: schemaTitle,
    description: schemaDescription,
    image: schemaImages,
    itemCondition: 'https://schema.org/UsedCondition',
    sku: currentListing.id.uuid,
    ...(specMake ? { brand: { '@type': 'Brand', name: String(specMake) } } : {}),
    ...(additionalProperties.length ? { additionalProperty: additionalProperties } : {}),
    // NOTE: We deliberately do NOT emit an aggregateRating here from the owner's
    // imported GOOGLE BUSINESS reviews. Two policy reasons (team-reviewed 2026-08-07):
    // (1) Google's review-snippet policy PROHIBITS marking up ratings aggregated
    //     from a third-party site (these are Google Maps reviews via Outscraper) —
    //     doing so risks a SITE-WIDE structured-data manual action.
    // (2) Entity mismatch: the rating is for the owner's BUSINESS, not this specific
    //     trailer PRODUCT. The Google reputation is still shown VISIBLY on-page
    //     (rating + count + link), which is transparent and policy-safe.
    // A compliant AggregateRating is a future play sourced from FIRST-PARTY on-platform
    // renter reviews of the actual listing (entity-matched, self-hosted).
    offers: offerSchema,
  };

  const listingCardImages = listingImages(currentListing, 'listing-card-2x');

  const claimListingModal = (
    <ListingClaimModal
      id="ListingClaimModal"
      isOpen={isClaimListingModalOpen}
      onCloseModal={() => setIsClaimListingModalOpen(!isClaimListingModalOpen)}
      onManageDisableScrolling={onManageDisableScrolling}
      listingCardImages={listingCardImages}
      currentListing={currentListing}
      saveEmailError={false}
      location={location}
    />
  );

  const authorInfo = extractListingAuthorInfo(userDetails);
  const socialFeeds = userDetails?.attributes?.profile?.publicData?.socialFeeds || [];

  const subscriptionProductData = getSubscriptionProductData(currentAuthor);
  const subscriptionName = publicData?.subscriptionProductName;

  // ===================================================================
  // Owner Insurance attestations (Listing Richness release, A4) — HAUL STAR
  // ONLY, render-if-present. Two INDEPENDENT owner self-attestations read from
  // the owner PROFILE publicData (Phase B builds the owner input; Phase A just
  // renders). NON-NEGOTIABLE: owner-attributed copy, NEVER "you're covered."
  //   insuredBusiness            = owner carries their OWN commercial coverage
  //                                (Phase B sets true only when a valid, non-
  //                                expired doc is on file). Optional display of
  //                                insuredBusinessExpiry.
  //   rentalInsuranceAvailable   = owner OFFERS renter coverage (simple toggle)
  // ===================================================================
  const ownerProfilePublicData =
    userDetails?.attributes?.profile?.publicData || {};
  const showInsuredBusiness = ownerProfilePublicData.insuredBusiness === true;
  const showRentalInsurance =
    ownerProfilePublicData.rentalInsuranceAvailable === true;

  // Available Discounts (Listing Richness) — Haul Star only, pure display/CTA.
  // As of 2026-09-27 the PDP no longer shows a discount IMAGE OVERLAY; instead
  // the discounts are listed as TEXT in a "Discounts Available" body section
  // (SectionDiscountsMaybe, between The Rundown and Trailer Details). This flag
  // gates that section; the section itself reads publicData.discounts.
  const isHaulStarListing = isHaulStarPlan(subscriptionName);

  // ===================================================================
  // Haul Star conversion redesign (2026-09-12). Haul Star listings get the
  // rebuilt action-center layout; free listings keep the legacy render.
  // ===================================================================
  const isHaulStarRedesign = isHaulStarPlan(subscriptionName);

  const viewedThisWeek = getViewsInLastDays(currentListing, 7);
  const showViewedCount = viewedThisWeek >= VIEWS_DISPLAY_THRESHOLD;

  const redesignTelLink = authorInfo?.phoneNumber ? formatTelLink(authorInfo.phoneNumber) : '';
  const redesignSmsLink = authorInfo?.phoneNumber ? formatSmsLink(authorInfo.phoneNumber) : '';

  // Short price for the compact card/sticky-bar ("$120"), derived from the
  // per-day starting price; falls back to the marketplace formatted price.
  const shortPriceText = starting
    ? formatMoney(intl, toMoney(starting.price, config)).replace(/\.00$/, '')
    : formattedPrice || '';

  // Track a special-card view via the existing analytics event system so it
  // becomes an owner proof stat (does a promo lift contact rate?). Memoized so
  // it is a stable reference and does not re-trigger the child's view effect on
  // every parent re-render.
  const specialHeadlineForTrack = publicData?.specialHeadline || undefined;
  const trackSpecialView = useCallback(
    () => trackEvent('special_viewed', { special_headline: specialHeadlineForTrack }),
    // trackEvent is stable within a render pass; headline rarely changes.
    [specialHeadlineForTrack]
  );

  // Google review block reused inside the redesigned trust area.
  const redesignHostInfoSection = (
    <div className={rd.rdContactCard}>
      {/* Google reviews at the TOP as a static trust badge (NOT clickable) — its
          only job is social proof; we deliberately keep zero side-doors off the
          contact actions so nothing competes with Call/Text/Message. */}
      {avgRating ? (
        <div className={rd.rdReviewTop}>
          <Icons name="star" />
          <span>
            <strong>{avgRating}</strong> from {reviewCount} Google reviews
          </span>
        </div>
      ) : null}

      <div className={rd.rdPriceRow}>
        <div>
          {shortPriceText ? (
            <>
              <span className={rd.rdPrice}>{shortPriceText}</span>{' '}
              <span className={rd.rdPriceUnit}>per day</span>
            </>
          ) : (
            <span className={rd.rdPrice}>Contact for pricing</span>
          )}
        </div>
        <span className={rd.rdHaulStarPill}>
          <Icons name="security" /> Haul Star
        </span>
      </div>

      {/* Available-to-Contact badge (V1 static, grounded wording) */}
      <div className={rd.rdAvailableBadge}>
        <span className={rd.rdAvailableDot} />
        <span className={rd.rdAvailableBadgeText}>
          <span className={rd.rdAvailableBadgeTitle}>Available to Contact</span>
          <span className={rd.rdAvailableBadgeSub}>Typically responds within a few hours</span>
        </span>
      </div>

      {/* Viewed X times this week (only above the social-proof threshold) */}
      {showViewedCount ? (
        <span className={rd.rdViewedPill}>
          <Icons name="eyeOpen" /> Viewed {viewedThisWeek} times this week
        </span>
      ) : null}

      {/* CTA stack: Call / Text / Send Message */}
      <div className={rd.rdCtaStack}>
        {redesignTelLink ? (
          <a
            className={rd.rdCtaPrimary}
            href={redesignTelLink}
            onClick={() => trackEvent(LISTING_ANALYTICS_EVENTS.CLICK_PHONE)}
          >
            <Icons name="phoneFilled" /> Call Owner
          </a>
        ) : null}
        {redesignSmsLink ? (
          <a
            className={rd.rdCtaSecondary}
            href={redesignSmsLink}
            onClick={() => trackEvent(LISTING_ANALYTICS_EVENTS.CLICK_PHONE, { method: 'text' })}
          >
            <Icons name="message" /> Text Owner
          </a>
        ) : null}
        <button type="button" className={rd.rdCtaGhost} onClick={onContactUser}>
          <Icons name="envelope" /> Send Message
        </button>
      </div>

      {/* Direct contact rows (email / website) */}
      {(authorInfo?.email || authorInfo?.website) ? (
        <div className={rd.rdContactRows}>
          {authorInfo?.email ? (
            <a
              href={`mailto:${authorInfo.email}`}
              className={rd.rdContactRow}
              onClick={() => trackEvent(LISTING_ANALYTICS_EVENTS.CLICK_EMAIL)}
            >
              <Icons name="email" /> {authorInfo.email}
            </a>
          ) : null}
          {authorInfo?.website ? (
            <a
              href={normalizeUrl(authorInfo.website)}
              target="_blank"
              rel="noopener noreferrer"
              className={rd.rdContactRow}
              onClick={() => trackEvent(LISTING_ANALYTICS_EVENTS.CLICK_WEBSITE)}
            >
              <Icons name="website" /> {authorInfo.website}
            </a>
          ) : null}
        </div>
      ) : null}

      {/* Social icons (Haul Star) */}
      {Array.isArray(socialFeeds) && socialFeeds.length > 0 ? (
        <div className={rd.rdSocialWrap}>
          <h3 className={rd.rdSocialHeader}>
            <FormattedMessage id="ListingPage.socialMedia" />
          </h3>
          <div className={rd.rdSocialIcons}>
            {socialFeeds.map((feed, index) => {
              if (!feed) return null;
              const platform = feed.platform || feed.name || '';
              const url = (feed.url || feed.link || '').trim();
              const IconComponent = getSocialIconComponent(platform);
              const key = `${platform || 'social'}-${index}`;
              if (!url) {
                return (
                  <span key={key} className={rd.rdSocialIcon}>
                    <IconComponent />
                  </span>
                );
              }
              return (
                <a
                  key={key}
                  href={normalizeUrl(url)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={rd.rdSocialIcon}
                  onClick={() =>
                    trackEvent(LISTING_ANALYTICS_EVENTS.CLICK_SOCIAL, {
                      platform: (platform || '').toLowerCase(),
                      url,
                    })
                  }
                  aria-label={platform}
                  title={platform}
                >
                  <IconComponent />
                </a>
              );
            })}
          </div>
        </div>
      ) : null}

      {/* No-fee trust block */}
      <div className={rd.rdTrustBox}>
        <Icons name="checkMark" />
        <span>No BigLoads booking fees. Contact the owner directly and rent with confidence.</span>
      </div>

      {/* Owner Insurance attestations (Listing Richness A4) — Haul Star only,
          render-if-present. Owner-attributed self-attestations; deliberately
          worded so BigLoads never claims the renter "is covered." Each pill is
          independent (2b does not require 2a). */}
      {isHaulStarRedesign && (showInsuredBusiness || showRentalInsurance) ? (
        <div className={rd.rdInsuranceBox}>
          {showInsuredBusiness ? (
            <div className={rd.rdInsurancePill}>
              <Icons name="security" />
              <span className={rd.rdInsurancePillText}>
                <span className={rd.rdInsurancePillTitle}>Insured Business</span>
                <span className={rd.rdInsurancePillSub}>
                  This owner has provided proof of commercial insurance.
                  {ownerProfilePublicData.insuredBusinessExpiry
                    ? ` Valid through ${formatInsuranceExpiry(
                        ownerProfilePublicData.insuredBusinessExpiry
                      )}.`
                    : ''}
                </span>
              </span>
            </div>
          ) : null}
          {showRentalInsurance ? (
            <div className={rd.rdInsurancePill}>
              <Icons name="clipboardCheck" />
              <span className={rd.rdInsurancePillText}>
                <span className={rd.rdInsurancePillTitle}>
                  Rental Insurance Available
                </span>
                <span className={rd.rdInsurancePillSub}>
                  Ask this owner about coverage options for your rental.
                </span>
              </span>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );

  const renderSimilarListings = () => {
    if (!Array.isArray(similarListings) || similarListings.length === 0) {
      return null;
    }

    return (
      <div className={css.nearbyListingsSection}>
        <h2 className={css.sectionHeading}>
          <FormattedMessage id="ListingPage.nearbyListingsHeading" />
        </h2>
        <div className={css.nearbyListingsGrid}>
          {similarListings.map(listing => {
            if (!listing || !listing.id) return null;

            return (
              <ListingCard
                className={css.listingCard}
                key={listing.id.uuid}
                listing={listing}
                renderSizes={cardRenderSizes(false)}
                setActiveListing={() => {}}
              />
            );
          })}
        </div>
      </div>
    );
  };

  const googleReviewSummaryContent = (
    <>
      <ReviewRating
        reviewStarClassName={css.reviewStar}
        className={css.reviewStars}
        rating={avgRating}
      />
      <FormattedMessage
        id="ListingPage.googleReviewSummary"
        values={{ rating: avgRating, count: reviewCount }}
      />
    </>
  );

  const googleReviewRatingSection = avgRating ? (
    <div className={css.googleReviewRatingSection}>
      {/* Link the rating out to the Google source so viewers can verify the
          business it belongs to (spoofing guard). Falls back to plain text if
          no source URL was stored (legacy imports). */}
      {googleSourceUrl ? (
        <ExternalLink
          href={googleSourceUrl}
          className={css.googleReviewRating}
          title={
            googleBusinessName
              ? `View ${googleBusinessName} on Google`
              : 'View on Google'
          }
        >
          {googleReviewSummaryContent}
        </ExternalLink>
      ) : (
        <div className={css.googleReviewRating}>{googleReviewSummaryContent}</div>
      )}
      {googleBusinessName ? (
        <div className={css.googleReviewBusiness}>{googleBusinessName}</div>
      ) : null}
    </div>
  ) : null;

  // extracted host info + map sections so we can reorder on mobile
  const hostInfoSection = (
    <div className={css.hostInfoSection}>
      {subscriptionProductData?.subscriptionProductName !== SUBSCRIPTION_PLAN_NAMES?.FREE &&
      subscriptionName ? (
        <>
          <div className={css.hostInfoWrapper}>
            {subscriptionName.toLowerCase().includes('haul') ? googleReviewRatingSection : null}

            <PrimaryButton className={css.contactHostBtn} onClick={onContactUser}>
              <FormattedMessage id="ListingPage.contactOrMessageOwner" />
            </PrimaryButton>

            <div className={css.pills}>
              {startingPriceText ? <span>{startingPriceText}</span> : null}
            </div>

            {subscriptionName ? (
              <div className={css.pills}>
                <span>
                  <Icons name={getSubscriptionIcon(subscriptionName)} />
                  {getSubscriptionLabel(subscriptionName)}
                </span>
              </div>
            ) : null}

            {/* ✅ Phone (click + track) */}
            {authorInfo?.phoneNumber ? (
              <a
                href={formatTelLink(authorInfo.phoneNumber)}
                className={css.contactLink}
                onClick={() => trackEvent(LISTING_ANALYTICS_EVENTS.CLICK_PHONE)}
              >
                <Icons name="phoneFilled" /> {authorInfo.phoneNumber}
              </a>
            ) : null}

            {/* ✅ Email (click + track) */}
            {authorInfo?.email ? (
              <a
                href={`mailto:${authorInfo.email}`}
                className={css.contactLink}
                onClick={() => trackEvent(LISTING_ANALYTICS_EVENTS.CLICK_EMAIL)}
              >
                <Icons name="email" /> {authorInfo.email}
              </a>
            ) : null}

            {/* ✅ Website (click + track) */}
            {authorInfo?.website ? (
              <a
                href={normalizeUrl(authorInfo.website)}
                target="_blank"
                rel="noopener noreferrer"
                className={css.contactLink}
                onClick={() => trackEvent(LISTING_ANALYTICS_EVENTS.CLICK_WEBSITE)}
              >
                <Icons name="website" /> {authorInfo.website}
              </a>
            ) : null}

            {Array.isArray(socialFeeds) &&
              socialFeeds.length > 0 &&
              isHaulStarPlan(subscriptionName) && (
                <div className={css.socialMediaWrap}>
                  <h3 className={css.socialMediaHeader}>
                    {' '}
                    <FormattedMessage id="ListingPage.socialMedia" />
                  </h3>

                  <div className={css.socialIcons}>
                    {socialFeeds.map((feed, index) => {
                      if (!feed) return null;

                      const platform = feed.platform || feed.name || '';
                      const url = (feed.url || feed.link || '').trim();
                      const IconComponent = getSocialIconComponent(platform);
                      const key = `${platform || 'social'}-${index}`;

                      if (!url) {
                        return (
                          <span key={key} className={css.socialIcon}>
                            <IconComponent />
                          </span>
                        );
                      }

                      return (
                        <a
                          key={key}
                          href={normalizeUrl(url)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={css.socialIconLink}
                          onClick={() =>
                            trackEvent(LISTING_ANALYTICS_EVENTS.CLICK_SOCIAL, {
                              platform: (platform || '').toLowerCase(),
                              url,
                            })
                          }
                          aria-label={platform}
                          title={platform}
                        >
                          <IconComponent />
                        </a>
                      );
                    })}
                  </div>
                </div>
              )}
          </div>

          {subscriptionProductData?.subscriptionProductName ===
            SUBSCRIPTION_PLAN_NAMES?.BIG_RIG && (
            <div className={css.listingWrapper}>
              <div className={css.authorAvatar}>
                <Avatar className={css.avatar} user={currentListing?.author} />
                <div className={css.avatarName}>
                  <h2>{authorInfo?.displayName}</h2>
                  <p>{authorInfo?.phoneNumber}</p>
                </div>
              </div>
              <div className={css.authorListings}>
                <span>
                  <Icons name="website" /> {authorInfo?.website}
                </span>
                <NamedLink
                  className={css.link}
                  name="ProfilePage"
                  params={{ id: currentListing?.author?.id?.uuid }}
                >
                  <FormattedMessage id="ListingPage.viewAllListings" />
                  <Icons name="rightArrow" />
                </NamedLink>
              </div>
            </div>
          )}
        </>
      ) : (
        <>
          <PrimaryButton className={css.contactHostBtn} onClick={onContactUser}>
            <FormattedMessage id="ListingPage.contactOrMessageOwner" />
          </PrimaryButton>

          {startingPriceText ? (
            <div className={css.pills}>
              <span>{startingPriceText}</span>
            </div>
          ) : null}
        </>
      )}
    </div>
  );

  const mapSection = (
    <SectionMapMaybe
      geolocation={geolocation}
      publicData={publicData}
      listingId={currentListing.id}
      mapsConfig={config.maps}
    />
  );

  const authorMaybe = (
    <SectionAuthorMaybe
      title={title}
      listing={currentListing}
      authorDisplayName={authorDisplayName}
      onContactUser={onContactUser}
      isInquiryModalOpen={isAuthenticated && inquiryModalOpen}
      onCloseInquiryModal={() => setInquiryModalOpen(false)}
      sendInquiryError={sendInquiryError}
      sendInquiryInProgress={sendInquiryInProgress}
      onSubmitInquiry={onSubmitInquiry}
      currentUser={currentUser}
      subscriptionName={subscriptionName}
      onManageDisableScrolling={onManageDisableScrolling}
    />
  );

  return (
    <Page
      title={schemaTitle}
      scrollingDisabled={scrollingDisabled}
      author={authorDisplayName}
      description={metaDescription}
      facebookImages={facebookImages}
      twitterImages={twitterImages}
      canonicalUrl={canonicalURL}
      schema={productSchema}
    >
      <LayoutSingleColumn className={css.pageRoot} topbar={topbar} footer={<FooterContainer />}>
        <div className={css.mainContentWrapper}>
          <div className={css.listingGallarySection} id="gallery">
            {currentListing.id && noPayoutDetailsSetWithOwnListing ? (
              <ActionBarMaybe
                className={css.actionBarForProductLayout}
                isOwnListing={isOwnListing}
                listing={currentListing}
                showNoPayoutDetailsSet={noPayoutDetailsSetWithOwnListing}
              />
            ) : null}

            {currentListing.id ? (
              <ActionBarMaybe
                className={css.actionBarForProductLayout}
                isOwnListing={isOwnListing}
                listing={currentListing}
                editParams={{
                  id: listingId.uuid,
                  slug: listingSlug,
                  type: listingPathParamType,
                  tab: listingTab,
                }}
              />
            ) : null}

            <SectionGallery
              listing={currentListing}
              onManageDisableScrolling={onManageDisableScrolling}
              variantPrefix={config.layout.listingImage.variantPrefix}
            />
          </div>

          <div className={css.contentWrapperForProductLayout}>
            <div className={css.mainColumnForProductLayout} id="details">
              <div className={css.detailSecHead}>
                <H4 as="h1" className={css.orderPanelTitle}>
                  <FormattedMessage id="ListingPage.orderTitle" values={{ title: richTitle }} />
                </H4>
                <p className={css.location}>
                  <Icons name="loction" /> {publicData?.location?.address}
                </p>
              </div>

              {isMobileLayout && isHaulStarRedesign ? (
                <>
                  <SectionSpecialMaybe
                    key="special-mobile"
                    publicData={publicData}
                    onView={trackSpecialView}
                  />
                  {redesignHostInfoSection}
                </>
              ) : isMobileLayout ? (
                hostInfoSection
              ) : null}

              <div className={css.otherDetails}>
                <CustomListingFields
                  publicData={publicData}
                  metadata={metadata}
                  listingFieldConfigs={listingConfig.listingFields}
                  categoryConfiguration={config.categoryConfiguration}
                  intl={intl}
                  title={title}
                  priceText={formattedPrice}
                  priceUnit="day"
                  listingId={listingId?.uuid}
                  isHaulStar={isHaulStarListing}
                />
              </div>

              <SectionOptionalExtras publicData={publicData} />

              <SectionTextMaybe
                heading={<FormattedMessage id="ListingPage.descriptionHeading" />}
                text={description}
                showAsIngress
                publicData={publicData}
              />

              {isMobileLayout && mapSection}
              {isMobileLayout && authorMaybe}

              {/* Ready-to-rent CTA banner (Haul Star) after the details */}
              {isHaulStarRedesign ? (
                <SectionReadyToRent
                  onContact={onContactUser}
                  phoneNumber={authorInfo?.phoneNumber}
                  telLink={redesignTelLink}
                  onCallClick={() => trackEvent(LISTING_ANALYTICS_EVENTS.CLICK_PHONE)}
                />
              ) : null}

              {renderSimilarListings()}

              {/* Google reviews are now fetched via the Profile Settings flow only */}

              {subscriptionProductData?.subscriptionProductName ===
                SUBSCRIPTION_PLAN_NAMES?.BIG_RIG && (
                <SectionAuthorMaybe
                  title={title}
                  listing={currentListing}
                  authorDisplayName={authorDisplayName}
                  onContactUser={onContactUser}
                  isInquiryModalOpen={isAuthenticated && inquiryModalOpen}
                  onCloseInquiryModal={() => setInquiryModalOpen(false)}
                  sendInquiryError={sendInquiryError}
                  sendInquiryInProgress={sendInquiryInProgress}
                  onSubmitInquiry={onSubmitInquiry}
                  currentUser={currentUser}
                  onManageDisableScrolling={onManageDisableScrolling}
                />
              )}
            </div>

            <div className={css.sideColumnForProductLayoutMain}>
              {!isMobileLayout && isHaulStarRedesign ? (
                <>
                  {redesignHostInfoSection}
                  <SectionSpecialMaybe
                    key="special-desktop"
                    publicData={publicData}
                    onView={trackSpecialView}
                  />
                  {mapSection}
                  {authorMaybe}
                </>
              ) : !isMobileLayout ? (
                <>
                  {hostInfoSection}
                  {mapSection}
                  {authorMaybe}
                </>
              ) : null}

              {/* reviews / cancellation commented out as before */}
              {/* <div id="reviews">
                <SectionReviews reviews={reviews} fetchReviewsError={fetchReviewsError} />
              </div> */}
              {/* <SectionCancellation publicData={publicData} intl={intl} metadata={metadata} /> */}
            </div>
          </div>

          {/* Mobile bottom spacer so the sticky bar never covers footer content */}
          {isHaulStarRedesign && isMobileLayout ? (
            <div className={rd.rdMobileBottomSpacer} />
          ) : null}
        </div>

        {/* Sticky mobile contact bar (Haul Star) */}
        {isHaulStarRedesign && isMobileLayout ? (
          <SectionStickyContactBar
            priceText={shortPriceText}
            telLink={redesignTelLink}
            smsLink={redesignSmsLink}
            onContact={onContactUser}
            onCallClick={() => trackEvent(LISTING_ANALYTICS_EVENTS.CLICK_PHONE)}
            onTextClick={() => trackEvent(LISTING_ANALYTICS_EVENTS.CLICK_PHONE, { method: 'text' })}
          />
        ) : null}

        {claimListingModal}
      </LayoutSingleColumn>
    </Page>
  );
};

ListingPageComponent.propTypes = {
  history: shape({
    push: func.isRequired,
  }).isRequired,
  location: shape({
    search: string,
  }).isRequired,

  intl: intlShape.isRequired,

  config: object.isRequired,
  routeConfiguration: arrayOf(propTypes.route).isRequired,

  params: shape({
    id: string.isRequired,
    slug: string,
    variant: oneOf([LISTING_PAGE_DRAFT_VARIANT, LISTING_PAGE_PENDING_APPROVAL_VARIANT]),
  }).isRequired,

  isAuthenticated: bool.isRequired,
  currentUser: propTypes.currentUser,
  getListing: func.isRequired,
  getOwnListing: func.isRequired,
  onManageDisableScrolling: func.isRequired,
  scrollingDisabled: bool.isRequired,
  inquiryModalOpenForListingId: string,
  showListingError: propTypes.error,
  callSetInitialValues: func.isRequired,
  reviews: arrayOf(propTypes.review),
  fetchReviewsError: propTypes.error,
  monthlyTimeSlots: object,
  sendInquiryInProgress: bool.isRequired,
  sendInquiryError: propTypes.error,
  onSendInquiry: func.isRequired,
  onInitializeCardPaymentData: func.isRequired,
  onFetchTransactionLineItems: func.isRequired,
  lineItems: array,
  fetchLineItemsInProgress: bool.isRequired,
  fetchLineItemsError: propTypes.error,
  similarListings: array,
};

const EnhancedListingPage = props => {
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const intl = useIntl();
  const history = useHistory();
  const location = useLocation();

  const showListingError = props.showListingError;
  const isVariant = props.params?.variant != null;
  const currentUser = props.currentUser;

  if (isForbiddenError(showListingError) && !isVariant && !currentUser) {
    return (
      <NamedRedirect
        name="SignupPage"
        state={{ from: `${location.pathname}${location.search}${location.hash}` }}
      />
    );
  }

  const isPrivateMarketplace = config.accessControl.marketplace.private === true;
  const isUnauthorizedUser = currentUser && !isUserAuthorized(currentUser);
  const hasNoViewingRights = currentUser && !hasPermissionToViewData(currentUser);
  const hasUserPendingApprovalError = isErrorUserPendingApproval(showListingError);

  if ((isPrivateMarketplace && isUnauthorizedUser) || hasUserPendingApprovalError) {
    return (
      <NamedRedirect
        name="NoAccessPage"
        params={{ missingAccessRight: NO_ACCESS_PAGE_USER_PENDING_APPROVAL }}
      />
    );
  } else if (
    (hasNoViewingRights && isForbiddenError(showListingError)) ||
    isErrorNoViewingPermission(showListingError)
  ) {
    return (
      <NamedRedirect
        name="NoAccessPage"
        params={{ missingAccessRight: NO_ACCESS_PAGE_VIEW_LISTINGS }}
      />
    );
  }

  return (
    <ListingPageComponent
      config={config}
      routeConfiguration={routeConfiguration}
      intl={intl}
      history={history}
      location={location}
      showOwnListingsOnly={hasNoViewingRights}
      {...props}
    />
  );
};

const mapStateToProps = state => {
  const { isAuthenticated } = state.auth;
  const {
    showListingError,
    reviews,
    fetchReviewsError,
    monthlyTimeSlots,
    sendInquiryInProgress,
    sendInquiryError,
    lineItems,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    inquiryModalOpenForListingId,
    similarListings,
  } = state.ListingPage;

  const { currentUser, userDetails } = state.user;
  const {
    productsLoading,
    products,
    productsError,
    subscriptionCancelInProgress,
  } = state.SubscriptionPage;

  const getListing = id => {
    const ref = { id, type: 'listing' };
    const listings = getMarketplaceEntities(state, [ref]);
    return listings.length === 1 ? listings[0] : null;
  };

  const getOwnListing = id => {
    const ref = { id, type: 'ownListing' };
    const listings = getMarketplaceEntities(state, [ref]);
    return listings.length === 1 ? listings[0] : null;
  };

  return {
    isAuthenticated,
    currentUser,
    getListing,
    getOwnListing,
    scrollingDisabled: isScrollingDisabled(state),
    inquiryModalOpenForListingId,
    showListingError,
    reviews,
    fetchReviewsError,
    monthlyTimeSlots,
    lineItems,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    sendInquiryInProgress,
    sendInquiryError,
    userDetails,
    productsLoading,
    products,
    productsError,
    subscriptionCancelInProgress,
    similarListings,
  };
};

const mapDispatchToProps = dispatch => ({
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
  callSetInitialValues: (setInitialValues, values, saveToSessionStorage) =>
    dispatch(setInitialValues(values, saveToSessionStorage)),
  onFetchTransactionLineItems: params => dispatch(fetchTransactionLineItems(params)),
  onSendInquiry: (listing, message) => dispatch(sendInquiry(listing, message)),
  onInitializeCardPaymentData: () => dispatch(initializeCardPaymentData()),
  onFetchTimeSlots: (listingId, start, end, timeZone) =>
    dispatch(fetchTimeSlots(listingId, start, end, timeZone)),
  onCancelSubscription: body => dispatch(cancelSubscription(body)),
  onFetchCurrentUser: () => dispatch(fetchCurrentUser()),
});

const ListingPage = compose(
  connect(mapStateToProps, mapDispatchToProps),
  withViewport
)(EnhancedListingPage);

export default ListingPage;
